import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import querystring from 'querystring';
import moment from 'moment';

import { Spinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_BOOKINGS_WITH_MINORS, GET_STATIONS } from 'store/queries';
import { ACTION_CODES } from 'constants/errors';
import { historyShape } from 'constants/propTypes';
import BookingSearchTable from './BookingSearchTable';

import { BookingsWithMinorsSearchHeader } from './BookingsWithMinorsSearchResults.styles';
import { MINORS_AMOUNT_KEY } from 'constants/minors';
import { Column, Row } from 'components/shared/Layout';
import SearchByMinors from 'pages/BookingSearch/SearchByMinors';
import BookingSearchSummary from './BookingSearchSummary';

const dateFormat = 'ddd DD MMM YYYY';

const BookingsWithMinorsSearchResults = ({ history }) => {
  const {
    location: { search }
  } = history;

  const { period, stationUic } = querystring.parse(search.substr(1));

  const { data: { stations } = { stations: [] } } = useQuery(GET_STATIONS, { fetchPolicy: 'cache-only' });
  const {
    data: { bookingsWithMinors } = {},
    error,
    loading
  } = useQuery(GET_BOOKINGS_WITH_MINORS, {
    variables: { period, stationUic },
    fetchPolicy: 'network-only'
  });

  if (loading) {
    return (
      <Row height="100vh" data-testid="bookings-with-minors-loading">
        <Column alignItems="center" justifyContent="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/search/booking'
    });
  }

  const bookings = bookingsWithMinors.map(
    ({ customer, externalBookingReferences = [], additionalDetails = [], outbound, passengers, reference, status }) => {
      const passengerCount = passengers.length;

      const minorsAmount = additionalDetails?.find(({ key }) => key === MINORS_AMOUNT_KEY)?.value || 0;
      const firstName = customer?.firstName;
      const lastName = customer?.lastName;

      const departingTo = outbound[0]?.destination?.shortName;
      const departingFrom = outbound[0]?.origin?.shortName;
      const departingOnRaw = outbound[0]?.timing?.isoDate;

      const departingOn = departingOnRaw ? moment(departingOnRaw).format(dateFormat) : undefined;

      const masterReference = externalBookingReferences.reduce(
        (current, { source, externalId }) => (source === 'MASTER' ? externalId : current),
        undefined
      );

      return {
        reference,
        masterReference,
        status,
        minorsAmount,
        firstName,
        lastName,
        passengerCount,
        departingFrom,
        departingOn,
        departingTo
      };
    }
  );

  return (
    <>
      <BookingsWithMinorsSearchHeader>
        <div>
          <BookingSearchSummary
            data-testid="booking-search-summary"
            noOfPnrsFound={bookings.length}
            departureDate={period}
            departureDateFormat="MMM YYYY"
            departureStation={stations?.find(({ uic }) => uic === stationUic)?.name || ''}
          />
        </div>
        <SearchByMinors
          data-testid="search-by-minors"
          defaultPeriod={period}
          defaultStationUic={stationUic}
          onSubmitSearch={({ searchMinorsStation, searchMinorsPeriod }) => {
            history.push(`/bookings?period=${searchMinorsPeriod}&stationUic=${searchMinorsStation}`);
          }}
        />
      </BookingsWithMinorsSearchHeader>
      {Boolean(bookings.length) && (
        <BookingSearchTable
          data-testid="booking-search-table"
          bookings={bookings}
          defaultSortKey="departingOn"
          removeColumns={['paymentDue', 'returningOn', 'specialAssistance', 'agentOffice']}
        />
      )}
    </>
  );
};

BookingsWithMinorsSearchResults.propTypes = {
  history: historyShape,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  })
};

export default BookingsWithMinorsSearchResults;
